import { Badge, Button, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { SearchField } from '../components/ui/SearchField';
import { ICONS } from '../components/ui/icons';

interface Row {
  id: string; at: string; action: string; severity: string; entityType: string | null; entityId: string | null;
  summary: string; user: { id: string; nome: string; email: string } | null; ip: string | null; device: string | null;
}
interface Data {
  rows: Row[]; total: number; page: number; limit: number;
  members: Array<{ id: string; nome: string; email: string }>;
  stats: { deletions30: number; failedLogins30: number; accessIps30: number };
}

const TYPES: Array<[string, string]> = [
  ['', 'Tudo'], ['deletions', 'Exclusões'], ['access', 'Acessos'], ['failed', 'Falhas de login'],
  ['changes', 'Alterações'], ['creations', 'Criações'], ['exports', 'Exportações'],
];
const PERIODS: Array<[string, string]> = [['7', 'Últimos 7 dias'], ['30', 'Últimos 30 dias'], ['90', 'Últimos 90 dias'], ['365', 'Último ano']];
/** O `Select` do Cloudscape não aceita valor vazio — usamos uma sentinela. */
const ALL = '__all__';
const ACTION_BADGE: Record<string, { label: string; variant: 'red' | 'green' | 'blue' | 'yellow' | 'lightgray' }> = {
  DELETE: { label: 'Exclusão', variant: 'red' }, DATA_SUBJECT_ERASURE: { label: 'LGPD', variant: 'red' },
  LOGIN_SUCCESS: { label: 'Acesso', variant: 'green' }, LOGIN_FAILED: { label: 'Falha de login', variant: 'yellow' }, LOGOUT: { label: 'Saída', variant: 'lightgray' },
  CREATE: { label: 'Criação', variant: 'blue' }, UPDATE: { label: 'Alteração', variant: 'lightgray' }, EXPORT: { label: 'Exportação', variant: 'blue' },
};
/** Cores do `Badge` do Cloudscape: `lightgray` → `grey`, `yellow` → `severity-medium`. */
const BADGE_COLOR: Record<string, 'red' | 'green' | 'blue' | 'orange' | 'neutral'> = {
  red: 'red', green: 'green', blue: 'blue', yellow: 'orange', lightgray: 'neutral',
};

/** Organização › Auditoria: quem fez o quê, quando, de onde */
export function AuditoriaPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Data | null>(null);
  const [type, setType] = useState('');
  const [userId, setUserId] = useState('');
  const [period, setPeriod] = useState('30');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Busca só depois de parar de digitar
  useEffect(() => { const t = setTimeout(() => { setTerm(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (type) p.set('type', type);
    if (userId) p.set('userId', userId);
    if (period) p.set('from', new Date(Date.now() - Number(period) * 86_400_000).toISOString());
    if (term) p.set('q', term);
    return p;
  }, [type, userId, period, term]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = params(); p.set('page', String(page));
      const res = await fetchApi(`/api/auditoria?${p}`);
      if (res.ok) setData(await res.json());
      else toast.error('Não foi possível carregar a auditoria');
    } finally {
      setLoading(false);
    }
  }, [fetchApi, params, page, toast]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await fetchApi(`/api/auditoria/export.csv?${params()}`);
    if (!res.ok) { toast.error('Não foi possível exportar'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!can('members:manage')) {
    return <div className="page-content"><div>Só administradores da organização veem a auditoria.</div></div>;
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const filtered = !!(type || userId || term || period !== '30');

  const typeOptions = TYPES.map(([v, l]) => ({ value: v || ALL, label: l }));
  const memberOptions = [{ value: ALL, label: 'Todas as pessoas' }, ...(data?.members || []).map((m) => ({ value: m.id, label: m.nome }))];
  const periodOptions = PERIODS.map(([v, l]) => ({ value: v, label: l }));

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }} className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Auditoria</h2>
          <div style={{ color: 'var(--text-muted)', maxWidth: 760 }}>
            Tudo o que foi criado, alterado e excluído na organização, os acessos (inclusive tentativas que falharam), com o IP e o aparelho de cada um. Os registros ficam guardados por 1 ano.
          </div>
        </div>
        <Button variant="secondary" icon={ICONS.Download} onClick={exportCsv}>Exportar CSV</Button>
      </div>

      <div className="page-content space-y-6">
        {data && (
          <div className="tools-stats">
            <button type="button" className="tools-stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => { setType('deletions'); setPeriod('30'); setPage(1); }}>
              <small>Exclusões em 30 dias</small><strong style={{ color: data.stats.deletions30 ? 'var(--crm-text-error)' : undefined }}>{data.stats.deletions30}</strong>
            </button>
            <button type="button" className="tools-stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => { setType('failed'); setPeriod('30'); setPage(1); }}>
              <small>Falhas de login em 30 dias</small><strong style={{ color: data.stats.failedLogins30 ? 'var(--crm-text-warning)' : undefined }}>{data.stats.failedLogins30}</strong>
            </button>
            <div className="tools-stat"><small>IPs diferentes com acesso em 30 dias</small><strong>{data.stats.accessIps30}</strong></div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-full sm:min-w-[280px]">
            <SearchField ariaLabel="Buscar na auditoria" placeholder="Buscar por item, pessoa, e-mail ou IP..." value={q} onChange={setQ} />
          </div>
          <div className="w-full sm:w-48">
            <Select value={(typeOptions.find((o) => o.value === (type || ALL)) ?? null)?.value ?? null} items={typeOptions} onValueChange={(value: string | null) => {
                const v = value;
                if (!v) return;
                setType(v === ALL ? '' : v);
                setPage(1);
              }} aria-label="Tipo" />
          </div>
          <div className="w-full sm:w-52">
            <Select value={(memberOptions.find((o) => o.value === (userId || ALL)) ?? null)?.value ?? null} items={memberOptions} onValueChange={(value: string | null) => {
                const v = value;
                if (!v) return;
                setUserId(v === ALL ? '' : v);
                setPage(1);
              }} aria-label="Pessoa" />
          </div>
          <div className="w-full sm:w-44">
            <Select value={(periodOptions.find((o) => o.value === period) ?? null)?.value ?? null} items={periodOptions} onValueChange={(value: string | null) => {
                const v = value;
                if (!v) return;
                setPeriod(v);
                setPage(1);
              }} aria-label="Período" />
          </div>
          {filtered && <Button variant="secondary" onClick={() => { setType(''); setUserId(''); setPeriod('30'); setQ(''); setPage(1); }}>Limpar filtros</Button>}
        </div>

        <div className="brand-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="audit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 16px', fontWeight: 600 }}>Quando</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Quem</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>O que</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>IP</th>
                <th style={{ padding: '10px 16px 10px 8px', fontWeight: 600 }}>Aparelho</th>
              </tr>
            </thead>
            <tbody>
              {data?.rows.map((r) => {
                const b = ACTION_BADGE[r.action] || { label: r.action, variant: 'lightgray' as const };
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }} title={new Date(r.at).toLocaleString('pt-BR')}>
                      {new Date(r.at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      {r.user ? <><div>{r.user.nome}</div><div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.user.email}</div></> : <span style={{ color: 'var(--text-muted)' }}>Sistema</span>}
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      <Badge variant={BADGE_COLOR[b.variant]}>{b.label}</Badge>
                      <div style={{ marginTop: 4 }}>{r.summary}</div>
                    </td>
                    <td style={{ padding: '10px 8px', fontFamily: 'var(--font-mono)', fontSize: 12, whiteSpace: 'nowrap' }}>{r.ip || '—'}</td>
                    <td style={{ padding: '10px 16px 10px 8px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.device || '—'}</td>
                  </tr>
                );
              })}
              {data && data.rows.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>{loading ? 'Carregando…' : 'Nada encontrado com esses filtros.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > data.limit && (
          <div className="flex items-center justify-between">
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{data.total.toLocaleString('pt-BR')} registros · página {page} de {pages}</div>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
              <Button variant="secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>Próxima</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
